"use client";

import * as React from "react";
import { Activity, ArrowRight, CircleCheck as CheckCircle2, FileCheck2, ShieldCheck, Sparkles, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
const primaryServices = [{
  id: 1,
  title: "Qualification & Validation",
  badge: "Compliance & Audit Readiness",
  description: "Comprehensive compliance packages designed for regulated pharmaceutical, biopharma, and testing laboratories across Ireland and the UK.",
  icon: FileCheck2,
  link: "/QualificationLifecycleServicesPage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/qualification_validation_lab.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/qualification_validation_lab.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/qualification_validation_lab.png-wvc-sizes",
  features: ["IQ/OQ/PQ protocols tailored to instrument specifics", "GAMP 5 aligned documentation and data integrity verification", "Method transfer, verification, and operator training", "Annual re-qualification and change control documentation"]
}, {
  id: 2,
  title: "Preventive Maintenance & Support",
  badge: "Maximum Uptime",
  description: "Scheduled maintenance, calibration, and rapid emergency intervention ensuring your analytical instrumentation performs reliably under heavy workflows.",
  icon: Wrench,
  link: "/PreventiveMaintenanceAndRepairsPage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/preventive_maintenance_engineer.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/preventive_maintenance_engineer.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/preventive_maintenance_engineer.png-wvc-sizes",
  features: ["Periodic optical calibration and wavelength verification", "Original manufacturer spare parts and certified consumables", "Rapid on-site response and diagnostic breakdown repair", "Customizable service level agreements (SLAs)"]
}];
const lifecycleBenefits = ["End-to-end support from installation to decommissioning", "Dedicated local application specialists & certified engineers", "Guaranteed compliance with MHRA, HPRA, and FDA standards", "Priority technical support desk and fast ticket resolution"];
export default function ServicesOverview() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: prefersReducedMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.15,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: prefersReducedMotion ? 1 : 0,
      y: prefersReducedMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="servicesoverview" data-nav="light" className="bg-muted/30 py-16 md:py-24 border-y border-border">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="outline" className="mb-3 px-3 py-1 font-medium bg-background text-foreground border-border">
            Laboratory & Process Support
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default">
            Expert Lifecycle Services for Regulated Environments
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            Protect analytical accuracy, regulatory standing, and instrument uptime with dedicated engineering and compliance support across Ireland and the UK.
          </p>
        </div>

        {/* 8/4 Split Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          {/* Left Column - 8 Cols */}
          <div className="lg:col-span-8 flex flex-col gap-8">
            {primaryServices.map((service, index) => {
            const IconComponent = service.icon;
            return <motion.div key={service.id} data-index={index} variants={itemVariants}>
                  <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow] duration-200 overflow-hidden flex flex-col justify-between">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-0">
                      {/* Card Image */}
                      <div className="md:col-span-5 relative min-h-[220px] md:min-h-full bg-muted">
                        <img src={service.image} data-wvc-srcset={service.imageSrcset} data-wvc-sizes={service.imageSizes} alt={service.title} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
                        <div className="absolute top-4 left-4">
                          <span className="inline-flex items-center justify-center size-10 rounded-lg bg-background/95 backdrop-blur-xs text-primary shadow-xs border border-border/60">
                            <IconComponent className="size-5" />
                          </span>
                        </div>
                      </div>

                      {/* Card Content */}
                      <div className="md:col-span-7 flex flex-col justify-between p-6 sm:p-8">
                        <div>
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <Badge variant="secondary" className="text-xs font-medium">
                              {service.badge}
                            </Badge>
                          </div>
                          <CardTitle className="text-xl sm:text-2xl font-bold text-card-foreground mb-3 font-default">
                            {service.title}
                          </CardTitle>
                          <CardDescription className="text-sm sm:text-base text-muted-foreground mb-6 font-default">
                            {service.description}
                          </CardDescription>

                          <Separator className="my-4" />

                          <ul className="space-y-2.5 mb-6">
                            {service.features.map((feature, fIndex) => <li key={fIndex} data-index={fIndex} className="flex items-start gap-2.5 text-xs sm:text-sm text-foreground/90 font-default">
                                <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                                <span>{feature}</span>
                              </li>)}
                          </ul>
                        </div>

                        <div className="pt-2">
                          <Button asChild variant="outline" className="w-full sm:w-auto font-medium group">
                            <Link to={service.link}>
                              <span>Explore Details</span>
                              <ArrowRight className="size-4 ml-1.5 transition-transform group-hover:translate-x-1" />
                            </Link>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                </motion.div>;
          })}
          </div>

          {/* Right Column - 4 Cols (Summary Panel) */}
          <motion.div variants={itemVariants} className="lg:col-span-4 flex flex-col">
            <Card className="h-full border border-border bg-card shadow-sm flex flex-col justify-between p-6 sm:p-8 relative overflow-hidden">
              <div>
                <div className="size-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-6">
                  <ShieldCheck className="size-6" />
                </div>

                <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary uppercase tracking-wider mb-2">
                  <Sparkles className="size-3.5" />
                  <span>Integrated Coverage</span>
                </div>

                <CardTitle className="text-2xl font-bold text-card-foreground mb-4 font-default">
                  Complete Lifecycle Assurance
                </CardTitle>

                <p className="text-sm text-muted-foreground font-default leading-relaxed mb-6">
                  From initial project design and feasibility through qualification, routine maintenance, and hardware lifecycle transitions, our teams provide complete analytical reassurance.
                </p>

                <Separator className="mb-6" />

                <div className="space-y-4 mb-8">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-foreground">
                    Core Assurance Pillars
                  </h4>
                  <ul className="space-y-3">
                    {lifecycleBenefits.map((benefit, bIndex) => <li key={bIndex} data-index={bIndex} className="flex items-start gap-2.5 text-xs sm:text-sm text-foreground/90 font-default">
                        <div className="size-1.5 rounded-full bg-primary mt-2 shrink-0" />
                        <span>{benefit}</span>
                      </li>)}
                  </ul>
                </div>
              </div>

              <div className="space-y-3 pt-6 border-t border-border mt-auto">
                <Button asChild variant="default" className="w-full justify-center">
                  <Link to="/ServicesPage">
                    <span>View All Services</span>
                    <ArrowRight className="size-4 ml-1.5" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="w-full justify-center">
                  <Link to="/SupportRequestPage">
                    <Activity className="size-4 mr-1.5" />
                    <span>Request Service Support</span>
                  </Link>
                </Button>
              </div>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}